import * as React from 'react';

import type { ContentImportRun, Space } from '@gitbook/api-client';
import type { AnalyticsContextSpaceContent } from '@gitbook/app-analytics';
import {
    getPageComputationIndexes,
    isRevisionPageParent,
    type RevisionPage,
    type RevisionPageComputed,
    RevisionPageType,
    type RevisionTreePage,
    RootParentSymbol,
} from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';

import { ComputedPagesContext, ComputedPagesGroup } from './ComputedPagesGroup';
import { ImportPagesGroup } from './ImportPagesGroup';

type RevisionTreePageGroup = {
    computedPage?: RevisionPageComputed | undefined;
    importRun?: ContentImportRun;
    pages: RevisionTreePage[];
};

type PagesRendererInput = {
    groups: RevisionTreePageGroup[];
    numberOfPages: number;
};

/**
 * Render a list of pages, while grouping them by computed page.
 */
export function PagesRenderer(props: {
    pages: PagesRendererInput;
    space: Space;
    readOnly: boolean;
    analyticsContext: AnalyticsContextSpaceContent;
    /** Whether or not hidden pages are shown. Hidden pages will always be shown if readOnly is false. */
    showHiddenPages: boolean;
    className?: string;
    renderPage: (pageProps: {
        key: string;
        treePage: RevisionTreePage;
        index: number;
        readOnly: boolean;
        showIfHidden: boolean;
        numberOfPages: number;
        computedPage: RevisionPageComputed | undefined;
        importRun: ContentImportRun | undefined;
        isComputedRoot: boolean;
        isFirstOfGroup: boolean;
    }) => React.ReactNode;
}) {
    const {
        pages,
        readOnly,
        showHiddenPages: showIfHidden,
        renderPage,
        className,
        space,
        analyticsContext,
    } = props;

    const parentComputedPage = React.useContext(ComputedPagesContext);

    let index = 0;
    const entries = pages.groups.map((group, i) => {
        const groupKey =
            (group.importRun?.id ? `${group.importRun?.id}-${i}` : null) ??
            group.computedPage?.id ??
            group.pages[0].renderKey;
        const groupChildren = group.pages.map((treePage, groupPageIndex) => {
            const childIndex = index;
            index += 1;

            return renderPage({
                key: treePage.renderKey,
                treePage,
                index: childIndex,
                readOnly,
                showIfHidden,
                numberOfPages: pages.numberOfPages,
                isFirstOfGroup: groupPageIndex === 0,
                computedPage: group.computedPage,
                isComputedRoot: Boolean(group.computedPage && !parentComputedPage),
                importRun: group.importRun,
            });
        });

        if (group.computedPage) {
            return (
                <ComputedPagesGroup
                    key={groupKey}
                    space={space}
                    page={group.computedPage}
                    readOnly={readOnly}
                    analyticsContext={analyticsContext}
                    adaptiveContentContext={null}
                >
                    <div className={className}>{groupChildren}</div>
                </ComputedPagesGroup>
            );
        }

        if (group.importRun) {
            return (
                <ImportPagesGroup
                    key={groupKey}
                    importRun={group.importRun}
                    isEmpty={groupChildren.length === 0}
                >
                    <div className={className}>{groupChildren}</div>
                </ImportPagesGroup>
            );
        }

        return (
            <div key={groupKey} className={className}>
                {groupChildren}
            </div>
        );
    });

    return <>{entries}</>;
}

/**
 * Compute the pages to render, the result should be passed to the `PagesRenderer` component.
 */
export function usePagesToRender(props: {
    parent: {
        page: RevisionPage | null;
        pages: RevisionTreePage[];
    };
    /** Whether hidden pages are rendered. Defaults to false. */
    showHiddenPages?: boolean;
    importRuns: ContentImportRun[];
}) {
    const { parent, showHiddenPages = false, importRuns } = props;
    const { computations } = useHiveRevision();

    return React.useMemo(() => {
        const indexes = getPageComputationIndexes(computations);
        const result: PagesRendererInput = {
            groups: [],
            numberOfPages: 0,
        };

        const foundComputedPages = new Set<string>();
        const parentIndex = indexes.parentIndex.get(parent.page?.id ?? RootParentSymbol);

        let originalIndex = 0;

        const pushEmptyComputedPage = (computedPage: RevisionPageComputed) => {
            if (foundComputedPages.has(computedPage.id)) {
                throw new Error('Computed page already pushed');
            }

            foundComputedPages.add(computedPage.id);
            result.groups.push({ computedPage, pages: [] });
        };

        const pushPageFromComputed = ({
            computedPage,
            page,
        }: {
            computedPage: RevisionPageComputed;
            page: RevisionTreePage;
        }) => {
            const lastGroup = result.groups[result.groups.length - 1];

            if (lastGroup?.computedPage?.id === computedPage.id) {
                lastGroup.pages.push(page);
            } else {
                foundComputedPages.add(computedPage.id);
                result.groups.push({ computedPage, pages: [page] });
            }
        };

        const pushOriginalIndex = (index: number) => {
            const originalComputedPage = parentIndex?.get(index);
            const lastGroup = result.groups[result.groups.length - 1];

            if (originalComputedPage && lastGroup?.computedPage?.id !== originalComputedPage.id) {
                pushEmptyComputedPage(originalComputedPage);
            }
        };

        const incrementOriginalIndex = () => {
            originalIndex += 1;
            pushOriginalIndex(originalIndex);
        };

        // Start by pushing a potential empty group if we are starting with an empty computed page
        pushOriginalIndex(0);

        for (let index = 0; index < parent.pages.length; index += 1) {
            const page = parent.pages[index];

            if (page.page.type === RevisionPageType.Computed) {
                throw new Error('No computed pages should be present at this stage');
            }

            // Find the associated computed page, if this page is the result of a computation
            const computedPage = indexes.pagesOriginIndex.get(page.page.id) ?? null;

            const lastGroup = result.groups[result.groups.length - 1];

            if (computedPage) {
                if (lastGroup && lastGroup.computedPage?.id === computedPage.id) {
                    lastGroup.pages.push(page);
                } else {
                    // Entering a new computed page, we move to the next original index
                    if (lastGroup?.computedPage) {
                        incrementOriginalIndex();
                    }

                    pushPageFromComputed({ computedPage, page });
                }
            } else {
                if (lastGroup?.computedPage) {
                    // Escaping a computed page, we move to the next original index
                    incrementOriginalIndex();
                }

                if (!showHiddenPages && isRevisionPageParent(page.page) && page.page.hidden) {
                    // In read-only mode, we don't render hidden pages (unless asked for)
                    // to replicate the published content experience.
                } else {
                    const importRun = importRuns.find((run) =>
                        run.pages?.some((importedPage) => importedPage.id === page.page.id)
                    );
                    result.groups.push({ importRun, pages: [page] });
                }

                // We just pushed an original page, we need to move to the next original index
                incrementOriginalIndex();
            }
        }

        // Push a group for each computed page that was originally present
        // but don't have pages in the tree.
        if (parentIndex) {
            Array.from(parentIndex.entries())
                .sort((a, b) => a[0] - b[0])
                .forEach(([_index, computedPage]) => {
                    if (foundComputedPages.has(computedPage.id)) {
                        return;
                    }
                    pushEmptyComputedPage(computedPage);
                });
        }

        // If an import is running and has no page in the tree, we still want to show it.
        importRuns.forEach((run) => {
            if (
                run.status === 'pending' ||
                (run.status === 'in-progress' &&
                    !result.groups.some((g) => g.importRun?.id === run.id))
            ) {
                result.groups.push({ importRun: run, pages: [] });
            }
        });

        return mergeGroups(result);
    }, [parent.pages, parent.page?.id, computations, showHiddenPages, importRuns]);
}

function mergeGroups(result: PagesRendererInput) {
    const { groups } = result;

    // iterate from the end so splicing i+1 never skips work
    for (let i = groups.length - 2; i >= 0; i -= 1) {
        const current = groups[i];
        const next = groups[i + 1];

        if (
            !current.computedPage &&
            !next.computedPage &&
            current.importRun?.id === next.importRun?.id
        ) {
            current.pages.push(...next.pages);
            groups.splice(i + 1, 1);
        }
    }

    result.numberOfPages = groups.reduce((sum, g) => sum + g.pages.length, 0);
    return result;
}
